import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { avatarSrc } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Bell, Heart, MessageCircle, UserPlus, MessageSquare, AtSign, CheckCheck, Loader2, BadgeCheck, Tag, Sparkles, Repeat2 } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "./ui/dropdown-menu";

function timeAgo(iso) {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}

const ICON = {
  like: { Icon: Heart, color: "#E60023", fill: true },
  comment_like: { Icon: Heart, color: "#E60023", fill: true },
  comment: { Icon: MessageCircle, color: "#4599FF" },
  reply: { Icon: MessageCircle, color: "#4599FF" },
  follow: { Icon: UserPlus, color: "#31A24C" },
  message: { Icon: MessageSquare, color: "#0066FF" },
  mention: { Icon: AtSign, color: "#A855F7" },
  verification: { Icon: BadgeCheck, color: "#0066FF" },
  tag: { Icon: Tag, color: "#4599FF" },
  new_post: { Icon: Sparkles, color: "#00E5FF" },
  reblog: { Icon: Repeat2, color: "#00E676" },
};

export default function NotificationBell() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    let alive = true;
    const poll = () => api.get("/notifications/unread-count").then(({ data }) => alive && setUnread(data.count || 0)).catch(() => {});
    poll();
    const t = setInterval(poll, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [user]);

  if (!user) return null;

  const onOpenChange = async (o) => {
    setOpen(o);
    if (o) {
      setLoading(true);
      try { const { data } = await api.get("/notifications"); setItems(data); } catch { /* noop */ }
      setLoading(false);
    }
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((x) => x.map((n) => ({ ...n, read: true })));
  };

  const clickItem = (n) => {
    if (!n.read) { api.post(`/notifications/${n.id}/read`).catch(() => {}); setUnread((u) => Math.max(0, u - 1)); }
    setOpen(false);
    if (n.type === "follow") nav(`/u/${n.actor_username}`);
    else if (n.type === "verification") nav(`/u/${user?.username}`);
    else if (n.type === "message") nav("/chat");
    else if (n.media_id) nav(`/media/${n.media_id}`);
  };

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger data-testid="notif-bell" className="relative w-10 h-10 rounded-full hover:bg-[#141414] flex items-center justify-center text-[#FFFFFF] outline-none focus:ring-2 focus:ring-[#0066FF]">
        <Bell size={21} />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-[#E60023] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-[#0A0A0A] border-[#1C1C1F] text-[#FFFFFF] w-[360px] max-w-[calc(100vw-1rem)] p-0 shadow-[0_12px_28px_rgba(0,0,0,0.5)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1C1C1F]">
          <span className="font-bold text-lg">Notifications</span>
          <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-[#4599FF] hover:underline flex items-center gap-1"><CheckCheck size={13} /> Mark all read</button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[#0066FF]" size={22} /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 px-4">
              <Bell size={26} className="text-[#2A2A2E] mx-auto mb-2" />
              <p className="text-[#52525B] text-sm">No notifications yet.</p>
            </div>
          ) : (
            items.map((n) => {
              const cfg = ICON[n.type] || ICON.comment;
              const Ic = cfg.Icon;
              return (
                <button
                  key={n.id}
                  data-testid={`notif-item-${n.id}`}
                  onClick={() => clickItem(n)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#141414] ${n.read ? "" : "bg-[#0066FF]/10"}`}
                >
                  <div className="relative shrink-0">
                    {n.actor_avatar ? (
                      <img src={avatarSrc(n.actor_avatar)} alt="" className="w-11 h-11 rounded-full object-cover bg-[#141414]" />
                    ) : (
                      <div className="w-11 h-11 rounded-full bg-[#141414] flex items-center justify-center text-[#FFFFFF] font-semibold">{n.actor_username?.[0]?.toUpperCase()}</div>
                    )}
                    <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-[#0A0A0A]" style={{ background: cfg.color }}>
                      <Ic size={11} className="text-white" fill={cfg.fill ? "white" : "none"} />
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#FFFFFF] leading-snug">
                      <span className="font-semibold">@{n.actor_username}</span> {n.text}
                    </p>
                    <span className="text-[11px] text-[#52525B]">{timeAgo(n.created_at)}</span>
                  </div>
                  {!n.read && <span className="w-2.5 h-2.5 rounded-full bg-[#0066FF] shrink-0" />}
                </button>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
